import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, MapPin, Sparkles, LayoutGrid, GalleryHorizontalEnd, Map as MapIcon, Navigation, ChevronDown } from "lucide-react";
import { CITIES, CATEGORIES, DESTINATIONS, COORDS } from "../data/destinations";
import { useApp } from "../context/AppState";
import { distKm } from "../lib/format";
import DestinationCard from "../components/DestinationCard";
import SwipeDeck from "../components/SwipeDeck";
import MapView from "../components/MapView";
import RecommendModal from "../components/RecommendModal";

export default function Jelajah() {
  const navigate = useNavigate();
  const { state, toggleSaved } = useApp();
  const [city, setCity] = useState("Jakarta");
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState("Semua");
  const [view, setView] = useState("grid");
  const [recOpen, setRecOpen] = useState(false);
  const [userLoc, setUserLoc] = useState(null);
  const [sortNearest, setSortNearest] = useState(false);
  const [locBusy, setLocBusy] = useState(false);

  const saved = state?.saved || [];

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = DESTINATIONS.filter((d) => d.city === city)
      .filter((d) => (cat === "Semua" ? true : d.category === cat))
      .filter((d) => (q ? (d.name + d.area + d.category + d.desc).toLowerCase().includes(q) : true));
    if (sortNearest && userLoc) {
      list = list
        .map((d) => ({ ...d, _dist: distKm(userLoc, COORDS[d.id]) }))
        .sort((a, b) => (a._dist ?? 1e9) - (b._dist ?? 1e9));
    }
    return list;
  }, [city, query, cat, sortNearest, userLoc]);

  const locate = () => {
    if (sortNearest) { setSortNearest(false); return; }
    if (!navigator.geolocation) { toast.error("Perangkat tidak mendukung lokasi"); return; }
    setLocBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLoc([pos.coords.latitude, pos.coords.longitude]);
        setSortNearest(true);
        setLocBusy(false);
        toast.success("Diurutkan dari lokasi kalian 📍");
      },
      () => { setLocBusy(false); toast.error("Tidak bisa akses lokasi. Izinkan lokasi di browser."); },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const openDest = (d) => navigate(`/destinasi/${d.id}`);

  const handleSave = (id, forceSave = false) => {
    const isSaved = saved.includes(id);
    if (forceSave && isSaved) { toast("Sudah di simpanan 💕"); return; }
    toggleSaved(id);
    toast(isSaved ? "Dihapus dari simpanan" : "Ditambahkan ke simpanan 💕");
  };

  return (
    <div className="kk-reveal">
      {/* Hero */}
      <section className="relative overflow-hidden rounded-3xl bg-[#f6e6de]">
        <img src="/assets/destinations/hero-rooftop.png" alt=""
          className="absolute right-0 top-0 h-full w-full object-cover object-[70%_center] sm:w-3/5" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#fbeee7] via-[#fbeee7]/85 to-transparent sm:to-[#fbeee7]/10" />
        <div className="relative max-w-md p-6 sm:p-10">
          <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-[#c9566b]">
            <Sparkles className="h-3.5 w-3.5" /> Kencan berikutnya menanti
          </div>
          <h1 className="mt-3 text-3xl font-extrabold leading-[1.1] tracking-tight text-[#43323a] sm:text-4xl">
            Bingung mau kencan<br />ke mana hari ini?
          </h1>
          <p className="mt-3 max-w-sm text-[14px] leading-relaxed text-[#8d7478]">
            Pilih kota, temukan tempat penuh cerita, dan kumpulkan poin untuk merawat Mochi kalian.
          </p>
          <button
            onClick={() => setRecOpen(true)}
            data-testid="open-recommend"
            className="mt-5 flex items-center gap-2 rounded-full kk-coral-gradient px-5 py-3 text-[14px] font-bold text-white transition-transform active:scale-[0.98]"
          >
            <Sparkles className="h-4 w-4" /> Bingung? Bantu pilihkan
          </button>
        </div>
      </section>

      {/* Search bar */}
      <div className="mt-5 flex flex-col gap-3 rounded-2xl border border-[#efeae4] bg-white p-2.5 kk-shadow-sm sm:flex-row sm:items-center">
        <div className="flex items-center gap-2 border-b border-[#efeae4] px-3 pb-2.5 sm:border-b-0 sm:border-r sm:pb-0 sm:pr-4">
          <MapPin className="h-4 w-4 flex-none text-[#ff5a5f]" />
          <div className="relative flex-1">
            <select
              value={city}
              onChange={(e) => setCity(e.target.value)}
              data-testid="city-selector"
              className="w-full appearance-none bg-transparent pr-6 text-[14px] font-bold outline-none"
            >
              {CITIES.map((c) => <option key={c}>{c}</option>)}
            </select>
            <ChevronDown className="pointer-events-none absolute right-0 top-1/2 h-4 w-4 -translate-y-1/2 text-[#a89f98]" />
          </div>
        </div>
        <div className="flex flex-1 items-center gap-2 px-3">
          <Search className="h-4 w-4 flex-none text-[#a89f98]" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Cari tempat atau aktivitas…"
            data-testid="search-input"
            className="w-full bg-transparent py-1.5 text-[14px] outline-none placeholder:text-[#b3aaa4]"
          />
        </div>
      </div>

      {/* Category chips + view toggle */}
      <div className="mt-4 flex items-center gap-3">
        <div className="no-scrollbar -mx-1 flex flex-1 gap-2 overflow-x-auto px-1 pb-1">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              data-testid={`category-${c}`}
              className={`whitespace-nowrap rounded-full border px-4 py-2 text-[13px] font-semibold transition-colors ${
                cat === c ? "border-[#f0d3da] bg-[#ffebeb] text-[#ff5a5f]" : "border-[#efeae4] bg-white text-[#7c7277]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
        <div className="flex flex-none items-center gap-1 rounded-full border border-[#efeae4] bg-white p-1">
          <button onClick={() => setView("grid")} data-testid="view-grid"
            className={`grid h-8 w-8 place-items-center rounded-full ${view === "grid" ? "bg-[#ffebeb] text-[#ff5a5f]" : "text-[#a89f98]"}`}>
            <LayoutGrid className="h-4 w-4" />
          </button>
          <button onClick={() => setView("swipe")} data-testid="view-swipe"
            className={`grid h-8 w-8 place-items-center rounded-full ${view === "swipe" ? "bg-[#ffebeb] text-[#ff5a5f]" : "text-[#a89f98]"}`}>
            <GalleryHorizontalEnd className="h-4 w-4" />
          </button>
          <button onClick={() => setView("map")} data-testid="view-map"
            className={`grid h-8 w-8 place-items-center rounded-full ${view === "map" ? "bg-[#ffebeb] text-[#ff5a5f]" : "text-[#a89f98]"}`}>
            <MapIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Result count */}
      <div className="mt-4 flex items-center justify-between gap-2">
        <p className="text-[14px] font-bold text-[#1e1b18]">
          {results.length} tempat di {city}
        </p>
        <button
          onClick={locate}
          disabled={locBusy}
          data-testid="sort-nearest"
          className={`flex flex-none items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12px] font-bold transition-colors disabled:opacity-60 ${
            sortNearest ? "border-[#4f74c8] bg-[#eef3ff] text-[#4f74c8]" : "border-[#efeae4] bg-white text-[#7c7277]"
          }`}
        >
          <Navigation className={`h-3.5 w-3.5 ${sortNearest ? "fill-[#4f74c8]" : ""}`} />
          {locBusy ? "Mencari lokasi…" : sortNearest ? "Terdekat aktif" : "Urutkan dari terdekat"}
        </button>
      </div>

      {/* Results */}
      <div className="mt-4">
        {view === "map" ? (
          <MapView city={city} items={results} onOpen={openDest} userLoc={sortNearest ? userLoc : null} />
        ) : view === "swipe" ? (
          <SwipeDeck items={results} saved={saved} onSave={handleSave} onOpen={openDest} />
        ) : results.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-[#e5d9ce] bg-[#fdfbf7] px-6 py-16 text-center">
            <p className="text-[15px] font-bold">Belum ada yang cocok</p>
            <p className="mt-1 text-[13px] text-[#8a8278]">Coba ganti kategori atau kata kunci.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {results.map((d, i) => (
              <DestinationCard
                key={d.id}
                dest={d}
                index={i}
                saved={saved.includes(d.id)}
                onSave={handleSave}
                onOpen={openDest}
                distanceKm={sortNearest ? d._dist : null}
              />
            ))}
          </div>
        )}
      </div>

      <RecommendModal open={recOpen} onClose={() => setRecOpen(false)} city={city} onPick={openDest} />
    </div>
  );
}
